import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import LessonCalendar from "@/components/LessonCalendar";
import { Card } from "@/components/ui/card";
import {
  CalendarCheck, CalendarClock, UploadCloud, UserCheck, UserX, UserMinus,
  AlertTriangle, Mail, MailWarning, ShieldAlert, ArrowRight, CheckCircle2, ClipboardList, Users,
} from "lucide-react";

const STATUS_TONE = {
  "in svolgimento": "bg-emerald-50 text-emerald-700",
  programmato: "bg-sky-50 text-sky-700",
};

function ActiveCourseCard({ c }) {
  return (
    <Link to={`/corsi/${c.id}`} data-testid={`dash-course-${c.code}`}
      className="group block rounded-xl border border-slate-200 bg-white p-4 hover:border-[#008C45] hover:shadow-md transition-[box-shadow,border-color]">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-mono text-[11px] text-slate-400">{c.code}</span>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_TONE[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status}</span>
      </div>
      <div className="font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#008C45]">{c.title}</div>
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-[#008C45]" style={{ width: `${Math.min(100, c.progress_pct)}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
        <span className="tabular">{c.hours_done_hm} / {c.hours_total_hm} h</span>
        <span className="flex items-center gap-1"><Users size={12} /> {c.enrolled}</span>
      </div>
    </Link>
  );
}

function RegisterToUpdate({ r }) {
  const first = r.lessons[0];
  return (
    <Link to={`/corsi/${r.course_id}?tab=importa&lesson=${first.id}`} data-testid={`dash-register-${r.code}`}
      className="group flex items-center gap-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 hover:bg-amber-50 hover:border-amber-400 transition-colors">
      <div className="h-10 w-10 shrink-0 rounded-lg bg-amber-100 text-amber-700 grid place-items-center font-bold tabular">{r.count}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 truncate">{r.title}</div>
        <div className="text-xs text-slate-600 truncate">
          {r.count === 1 ? "Lezione da importare" : "Lezioni da importare"}: {r.lessons.slice(0, 4).map((l) => `n° ${l.number} (${fmtDateTime(l.date)})`).join(", ")}{r.count > 4 ? "…" : ""}
        </div>
      </div>
      <span className="hidden sm:flex items-center gap-1 text-sm font-medium text-amber-800 group-hover:translate-x-0.5 transition-transform">
        Importa presenze <ArrowRight size={15} />
      </span>
    </Link>
  );
}

function MiniStat({ icon: Icon, label, value, to, tone, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:border-slate-400 transition-colors">
      <Icon size={16} className={tone} />
      <span className="text-xs text-slate-500 flex-1">{label}</span>
      <span className="text-sm font-bold text-slate-900 tabular">{value}</span>
    </Link>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Caricamento…</div>;

  const secondary = [
    { title: "Lezioni", items: [
      { icon: CalendarCheck, label: "Lezioni svolte", value: d.lessons_done, to: "/corsi", tone: "text-emerald-600", testid: "kpi-card-lessons-done" },
      { icon: CalendarClock, label: "Lezioni programmate", value: d.lessons_planned, to: "/corsi", tone: "text-sky-600", testid: "kpi-card-lessons-planned" },
      { icon: UploadCloud, label: "Import da verificare", value: d.imports_pending, to: "/importazioni", tone: "text-amber-600", testid: "kpi-card-imports-pending" },
    ] },
    { title: "Partecipanti (ultima lezione)", items: [
      { icon: UserCheck, label: "Presenti", value: d.participants_present, to: "/segnalazioni", tone: "text-emerald-600", testid: "kpi-card-present" },
      { icon: UserMinus, label: "Presenza parziale", value: d.participants_partial, to: "/segnalazioni", tone: "text-amber-600", testid: "kpi-card-partial" },
      { icon: UserX, label: "Assenti", value: d.participants_absent, to: "/segnalazioni", tone: "text-rose-600", testid: "kpi-card-absent" },
    ] },
    { title: "Soglie e anomalie", items: [
      { icon: ShieldAlert, label: "Limite superato", value: d.over_threshold, to: "/segnalazioni", tone: "text-rose-600", testid: "kpi-card-over" },
      { icon: AlertTriangle, label: "Prossimi al limite", value: d.near_threshold, to: "/segnalazioni", tone: "text-orange-600", testid: "kpi-card-near" },
      { icon: AlertTriangle, label: "Anomalie rilevate", value: d.anomalies, to: "/segnalazioni", tone: "text-orange-600", testid: "kpi-card-anomalies" },
    ] },
    { title: "E-mail", items: [
      { icon: Mail, label: "E-mail inviate", value: d.emails_sent, to: "/email", tone: "text-slate-600", testid: "kpi-card-emails-sent" },
      { icon: MailWarning, label: "E-mail non consegnate", value: d.emails_failed, to: "/email", tone: "text-rose-600", testid: "kpi-card-emails-failed" },
    ] },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Corsi attivi e registri che richiedono un intervento." />

      <section className="mb-10" data-testid="dash-registers-section">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList size={18} className="text-amber-600" />
          <h2 className="font-display font-bold text-slate-900">Registri da aggiornare</h2>
          {d.registers_to_update.length > 0 && <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{d.registers_to_update.length}</span>}
        </div>
        {d.registers_to_update.length === 0 ? (
          <Card className="p-5 border-slate-200 flex items-center gap-3 text-sm text-slate-600" data-testid="dash-registers-empty">
            <CheckCircle2 size={18} className="text-emerald-600" /> Tutti i registri sono aggiornati.
          </Card>
        ) : (
          <div className="space-y-2">{d.registers_to_update.map((r) => <RegisterToUpdate key={r.course_id} r={r} />)}</div>
        )}
      </section>

      <section className="mb-10" data-testid="dash-calendar-section">
        <h2 className="font-display font-bold text-slate-900 mb-3">Calendario lezioni</h2>
        <LessonCalendar />
      </section>

      <section className="mb-12" data-testid="dash-active-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-slate-900">Corsi attivi <span className="text-slate-400 font-normal">({d.active_courses_list.length})</span></h2>
          <button onClick={() => navigate("/corsi")} data-testid="dash-all-courses-link" className="text-sm font-medium text-[#008C45] hover:underline flex items-center gap-1">
            Visualizza tutti i corsi <ArrowRight size={14} />
          </button>
        </div>
        {d.active_courses_list.length === 0 ? (
          <Card className="p-5 border-slate-200 text-sm text-slate-500">Nessun corso attivo.</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {d.active_courses_list.map((c) => <ActiveCourseCard key={c.id} c={c} />)}
          </div>
        )}
      </section>

      <section className="border-t border-slate-200 pt-6" data-testid="dash-secondary-section">
        <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Altre informazioni</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {secondary.map((g) => (
            <div key={g.title}>
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{g.title}</div>
              <div className="space-y-1.5">{g.items.map((it) => <MiniStat key={it.label} {...it} />)}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
